import { storagePage, formInformation } from './storage'
import { useHistory } from 'react-router'
import { FormInformation } from './types'

export default function useSubmitProduct() {
    const history = useHistory()
    const productId = storagePage.product?.id
    const isEdition = storagePage.mode === 'edition' && productId
    const endpoint = isEdition
        ? `/v5.6/apis/products/${productId}`
        : '/v5.6/apis/products'
    const { submit, validation, observer } = formInformation.useSubmit<any>(
        endpoint,
        {
            message: isEdition
                ? '¿Está seguro de actualizar el producto?'
                : '¿Está seguro de crear el producto?',
            method: isEdition ? 'put' : 'post',
            done: () => {
                history.push('/configuration/products')
            },
            onError: () => {
                // Error handling
            }
        }
    )

    validation((v, form) => {
        v.name
            .minLength(2, 'El número de caracteres válidos es 2 - 180')
            .isEmpty('Se requiere ingresar el nombre')
        v.type_product.isEmpty('Se requiere seleccionar el tipo de producto')
        v.category.isEmpty('Se requiere seleccionar la categoría')
        v.subcategory.isEmpty('Se requiere seleccionar la subcategoría')
        v.country_availability.isEmpty(
            'Se requiere seleccionar al menos un país de disponibilidad'
        )

        const typeProduct = form.type_product?.value
        const countryIds = (form.country_availability || [])
            .map(c => parseInt(c.value, 10))
            .filter(n => !Number.isNaN(n))
        const hasCountry = (countryId: number) => countryIds.includes(countryId)
        const fiscalRules = [
            {
                countryId: 2,
                field: 'fiscal_code_cr',
                message: 'Se requiere ingresar el código fiscal CR'
            },
            {
                countryId: 3,
                field: 'fiscal_code_pa',
                message: 'Se requiere ingresar el código fiscal PA'
            }
        ] as const

        const applyFiscalCodeRules = () => {
            fiscalRules.forEach(rule => {
                if (hasCountry(rule.countryId)) {
                    ;(v as any)[rule.field].isEmpty(rule.message)
                }
            })
        }

        if (typeProduct === '0') {
            v.manufacturer_code
                .minLength(2, 'El número de caracteres válidos es 2 - 180')
                .isEmpty('Se requiere ingresar el código')
            v.brand.isEmpty('Se requiere seleccionar la marca')
            applyFiscalCodeRules()
        } else if (typeProduct === '1') {
            v.manufacturer_code
                .minLength(2, 'El número de caracteres válidos es 2 - 180')
                .isEmpty('Se requiere ingresar el código')
            v.brand.isEmpty('Se requiere seleccionar la marca')
            v.presentation_unit.isEmpty(
                'Se requiere seleccionar la unidad de presentación'
            )
            v.presentation_quantity.isEmpty(
                'Se requiere ingresar la cantidad por presentación'
            )
            applyFiscalCodeRules()
        } else if (typeProduct === '2') {
            v.manufacturer_code
                .minLength(2, 'El número de caracteres válidos es 2 - 180')
                .isEmpty('Se requiere ingresar el código')
            applyFiscalCodeRules()
        }
    })

    observer((form: FormInformation) => {
        const typeProduct = parseInt(form.type_product?.value || '0')
        const categoryId = parseInt(form.category?.value || '0')
        const subcategoryId = parseInt(form.subcategory?.value || '0')

        const countryIds = (form.country_availability || [])
            .map(c => parseInt(c.value, 10))
            .filter(n => !Number.isNaN(n))

        let photoData: any = null
        if (form.photo) {
            if (typeof form.photo === 'object' && form.photo.drive_id) {
                photoData = {
                    photo: {
                        drive_id: form.photo.drive_id,
                        filename: form.photo.filename,
                        type: form.photo.type,
                        size: form.photo.size
                    }
                }
            } else if (typeof form.photo === 'string') {
                const photoId = parseInt(form.photo)
                if (!isNaN(photoId) && form.photo === photoId.toString()) {
                    photoData = { photo_file_id: photoId }
                } else {
                    let driveId: string | null = null
                    const driveIdMatch = form.photo.match(/[?&]id=([^&]+)/)
                    if (driveIdMatch) {
                        driveId = driveIdMatch[1]
                    } else if (form.photo.startsWith('http')) {
                        const urlParts = form.photo.split('id=')
                        if (urlParts.length > 1) {
                            driveId = urlParts[1].split('&')[0].split('/')[0]
                        }
                    } else {
                        driveId = form.photo
                    }

                    if (driveId) {
                        photoData = {
                            photo: {
                                drive_id: driveId,
                                filename: 'photo.jpg',
                                type: 'image/jpeg',
                                size: 0
                            }
                        }
                    }
                }
            }
        }

        const literatureFiles = form.literature
            ? form.literature
                  .filter(f => f.drive_id || f.id)
                  .map((f, idx) => {
                      if (f.id) {
                          return {
                              file_id: f.id,
                              sort_order: idx + 1
                          }
                      }
                      if (f.drive_id) {
                          return {
                              drive_id: f.drive_id,
                              filename: f.filename || 'literature.pdf',
                              type: f.type || 'application/pdf',
                              size: f.size || 0,
                              sort_order: idx + 1
                          }
                      }
                      return null
                  })
                  .filter(f => f !== null)
            : []

        let details: any = {
            description: form.name.trim()
        }

        if (typeProduct === 0) {
            details.manufacturer_code = form.manufacturer_code.trim()
            details = {
                ...details,
                measurement_range: form.measurement_range || null,
                equipment_model: form.model || null,
                brand_id: parseInt(form.brand?.value || '0'),
                impact_ecosystem_ids: (form.impact_ecosystem || [])
                    .map(o => parseInt(o.value, 10))
                    .filter(n => !Number.isNaN(n)),
                productive_process_ids: (form.productive_process || [])
                    .map(o => parseInt(o.value, 10))
                    .filter(n => !Number.isNaN(n)),
                vertical_market_ids: (form.vertical_market || [])
                    .map(o => parseInt(o.value, 10))
                    .filter(n => !Number.isNaN(n)),
                certification_ids: (form.certification || [])
                    .map(o => parseInt(o.value, 10))
                    .filter(n => !Number.isNaN(n)),
                service_ids: (form.services || [])
                    .map(o => parseInt(o.value, 10))
                    .filter(n => !Number.isNaN(n)),
                fiscal_code_ni: form.fiscal_code_ni || null,
                fiscal_code_cr: form.fiscal_code_cr || '',
                fiscal_code_pa: form.fiscal_code_pa || '',
                fiscal_code_ve: form.fiscal_code_ve || null
            }
        } else if (typeProduct === 1) {
            details = {
                ...details,
                manufacturer_code: form.manufacturer_code.trim(),
                composition: form.composition || null,
                brand_id: parseInt(form.brand?.value || '0'),
                presentation_unit_id: parseInt(
                    form.presentation_unit?.value || '0'
                ),
                useful_life_id: form.useful_life
                    ? parseInt(form.useful_life.value)
                    : null,
                packaging_id: form.packaging
                    ? parseInt(form.packaging.value)
                    : null,
                regulation_id: form.regulation
                    ? parseInt(form.regulation.value)
                    : null,
                impact_ecosystem_ids: (form.impact_ecosystem || [])
                    .map(o => parseInt(o.value, 10))
                    .filter(n => !Number.isNaN(n)),
                productive_process_ids: (form.productive_process || [])
                    .map(o => parseInt(o.value, 10))
                    .filter(n => !Number.isNaN(n)),
                vertical_market_ids: (form.vertical_market || [])
                    .map(o => parseInt(o.value, 10))
                    .filter(n => !Number.isNaN(n)),
                certification_ids: (form.certification || [])
                    .map(o => parseInt(o.value, 10))
                    .filter(n => !Number.isNaN(n)),
                fiscal_code_ni: form.fiscal_code_ni || null,
                fiscal_code_cr: form.fiscal_code_cr || '',
                fiscal_code_pa: form.fiscal_code_pa || '',
                fiscal_code_ve: form.fiscal_code_ve || null,
                presentation_quantity: form.presentation_quantity
                    ? parseFloat(form.presentation_quantity)
                    : null,
                air_transport: form.air_transport || false,
                maritime_transport: form.maritime_transport || false,
                refrigeration: form.refrigeration || false
            }
        } else if (typeProduct === 2) {
            details = {
                ...details,
                manufacturer_code: form.manufacturer_code.trim(),
                certificate: form.certificate || false,
                fiscal_code_ni: form.fiscal_code_ni || null,
                fiscal_code_cr: form.fiscal_code_cr || '',
                fiscal_code_pa: form.fiscal_code_pa || '',
                fiscal_code_ve: form.fiscal_code_ve || null,
                certification_ids: (form.certification || [])
                    .map(o => parseInt(o.value, 10))
                    .filter(n => !Number.isNaN(n))
            }
        }

        return {
            type_id: typeProduct,
            category_id: categoryId,
            subcategory_id: subcategoryId,
            country_ids: countryIds,
            ...(photoData || {}),
            literature_files: literatureFiles,
            details
        }
    })

    return submit
}
